const en = {
  // Header / Hero
  hero: {
    title: "Curated AI agents. At your service.",
    subtitle: "One size fits none.",
  },

  // Theme toggle
  theme: {
    dark: "🌙 Dark Mode",
    light: "☀️ Light Mode",
  },

  // Error boundary
  errorBoundary: {
    title: "Something went wrong",
    message: "We're sorry, but something unexpected happened.",
    refresh: "Refresh Page",
  },

  // Agents section
  agents: {
    sectionTitle: "Meet our AI Agents",
    prevAgent: "Previous agent",
    nextAgent: "Next agent",
    sliderLabel: "AI agents slider",
    watchOnYoutube: "Watch on YouTube",
    comingSoon: "Coming Soon!",
  },

  // Agent options
  agentOptions: {
    proposeAgent: "Order Your Own Agent!",
    proposedTagline: "Fill out the form below to request your own, customized agent to solve your business needs!",
    mediShiftTagline: "AI-powered scheduling for healthcare teams",
    frostDeskTagline: "Turn technician work notes into a ready-to-act parts list — automatically.",
    pipTronixTagline: "From signed procedures to hands-free factory execution.",
  },

  // Order form section
  order: {
    sectionTitle: "Order an AI Agent",
    selectAgent: "Select an agent",
    proposeTitle: "Propose a New Agent",
    orderTitle: "Order This Agent",
    close: "Close",
    submit: "Submit request",
    submitting: "Submitting...",
    clearForm: "Clear form",
    successMessage: "Request sent successfully! We'll be in touch soon.",
  },

  // Form labels & placeholders – "Propose" form
  proposeForm: {
    proposedAgentName: "Proposed agent name *",
    proposedAgentNamePlaceholder: "e.g., RetailPulse",
    industryDomain: "Industry / domain",
    industryDomainPlaceholder: "e.g., Healthcare",
    contactName: "Your name *",
    contactNamePlaceholder: "Full name",
    contactEmail: "Email *",
    contactEmailPlaceholder: "you@company.com",
    company: "Company / organization",
    companyPlaceholder: "Company name",
    role: "Role / title",
    rolePlaceholder: "Your role",
    problemToSolve: "Problem to solve",
    problemToSolvePlaceholder: "What should this agent help with?",
    desiredCapabilities: "Desired capabilities",
    desiredCapabilitiesPlaceholder: "Key features, workflows, or tasks",
    dataSources: "Data sources or systems",
    dataSourcesPlaceholder: "EHRs, CRMs, documents, APIs, etc.",
    integrations: "Integrations required",
    integrationsPlaceholder: "Slack, Teams, Salesforce",
    successMetrics: "Success metrics",
    successMetricsPlaceholder: "Time saved, accuracy",
    timeline: "Timeline",
    timelinePlaceholder: "e.g., 6-8 weeks",
    budgetRange: "Budget range",
    budgetRangePlaceholder: "$5k-$20k",
    additionalNotes: "Additional notes",
    additionalNotesPlaceholder: "Anything else?",
  },

  // Form labels & placeholders – "Existing agent" form
  existingForm: {
    contactName: "Your name *",
    contactNamePlaceholder: "Full name",
    contactEmail: "Email *",
    contactEmailPlaceholder: "you@company.com",
    company: "Company / organization",
    companyPlaceholder: "Company name",
    role: "Role / title",
    rolePlaceholder: "Your role",
    useCase: "Intended use case",
    useCasePlaceholder: "What will it help with?",
    keyWorkflows: "Key workflows to automate",
    keyWorkflowsPlaceholder: "Scheduling, triage, etc.",
    dataSources: "Data sources or systems",
    dataSourcesPlaceholder: "EHRs, CRMs, documents, APIs, etc.",
    integrations: "Integrations required",
    integrationsPlaceholder: "Slack, Teams, calendars, etc.",
    expectedVolume: "Expected volume",
    expectedVolumePlaceholder: "e.g., 500 requests/week",
    timeline: "Timeline",
    timelinePlaceholder: "e.g., 4-6 weeks",
    budgetRange: "Budget range",
    budgetRangePlaceholder: "$5k-$20k",
    preferredContact: "Preferred contact method",
    preferredContactPlaceholder: "Email, phone, etc.",
    additionalNotes: "Additional notes",
    additionalNotesPlaceholder: "Anything else?",
  },

  // Validation
  validation: {
    nameRequired: "Name is required (minimum 2 characters)",
    emailRequired: "Valid email is required",
    proposedNameRequired: "Proposed agent name is required",
    fixErrors: "Please fix the form errors before submitting.",
  },

  // Error messages
  errors: {
    unableToSubmit: "Unable to submit right now. Please try again.",
    timeout: "Request timed out. Please check your connection and try again.",
    network: "Network error. Please check your internet connection and try again.",
  },

  // FAQ
  faq: {
    sectionTitle: "Frequently Asked Questions",
    items: [
      {
        question: "Who is behind CodeCat?",
        answer: "<strong>CodeCat is a two‑woman venture</strong> built on deep, hands‑on expertise. I'm a <strong>former Microsoft data scientist</strong> with a background spanning data science, product management, linguistics, and software engineering. After working across automotive, enterprise, healthcare, education, and financial sectors, I now focus on building <strong>custom AI agents that solve real problems</strong> and actually get work done.\n\n I'm supported by an exceptional QA partner with eight years of experience. She's <strong>meticulous, thorough, and relentlessly focused on user experience</strong>, ensuring every solution we deliver is polished, reliable, and a pleasure to use.",
      },
      {
        question: "What makes your AI agents different?",
        answer:
          "A lot of AI tools don't actually help because they're generic and don't match how your business really works. We build AI agents that are <strong>made specifically for you</strong>. They plug into the tools you already use, understand the language of your industry, and <strong>take over the repetitive tasks</strong> that eat up your time.<br><br>The result? <strong>Less manual work, fewer mistakes, faster turnaround times</strong>, and more space for you and your team to focus on the parts of the business that actually matter.",
      },
      {
        question: "How does the process work?",
        answer:
          "It starts with <strong>a conversation</strong>. You tell us about your workflow challenges, we propose a tailored agent solution, and once approved, we build, test, and deploy it. Most agents go live within <strong>4-8 weeks</strong> depending on complexity.",
      },
      {
        question: "What does it cost?",
        answer:
          "Pricing depends on the agent's complexity, integrations required, and support level. We offer <strong>flexible options</strong> from one-time builds to ongoing partnerships. Reach out for a <strong>personalized quote. No commitment required</strong>.",
      },
      {
        question: "Is my data secure?",
        answer:
          "Yes, your data is <strong>absolutely secure</strong>. We treat security as a core requirement, not an add‑on. Every AI agent we build follows strong <strong>industry‑standard practices</strong> for protecting your information, including encrypted data storage and strict access controls.<br><br>In simple terms: your data stays <strong>private, protected, and in your hands</strong>.",
      },
      {
        question: "Where are you located?",
        answer:
          "We're based in <strong>Canada</strong>, but we work with customers <strong>all over the world</strong>. Whether you're local or international, we can build and deploy AI agents for your business. <strong>Geography is no barrier.</strong>",
      },
      {
        question: "How do I get started?",
        answer:
          "Easy! Just <strong>select an agent above</strong> and fill out the request form, or propose your own idea. We'll review your submission and get back to you within <strong>1-2 business days</strong> to discuss next steps.",
      },
    ],
  },

  // Footer
  footer: {
    copyright: "© 2026 CodeCat. All rights reserved.",
  },
};

export default en;
